<template>
  <div class="container-fluid">
    <div class="row page-titles">
      <div class="col-md-5 col-8 align-self-center">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/home">Home</router-link>
          </li>
          <li class="breadcrumb-item active">シフト表</li>
        </ol>
      </div>
    </div>
    <div class="row">
      <div class="col-md-2 pr-10">
        <div class="mt-10 card">
          <div class="card-body">
            <div class="card-body">
              <div>
                <p class="mb-3 text-left">事業所</p>
                <select-office v-model="inputs.selectedOfficeId" :disabled="isProcessing"
                  :allowClear="false"
                  @update:modelValue="onOfficeSelected"></select-office>
              </div>
              <div class="pt-2" v-if="false">
                <p class="mb-0">利用者表示</p>
                <div class="switch pt-1">
                  <label>
                    全利用者
                    <input type="checkbox" v-model="inputs.userViewMode" @change.prevent="onChangeUserViewMode"><span
                      class="lever"></span>
                    担当利用者のみ
                  </label>
                </div>
                <div>
                  <p class="text-right">
                    <router-link :to="`/staff/${profile.id}`">担当利用者を設定する</router-link>
                  </p>
                </div>
              </div>
              <div class="pt-2">
                <p class="mb-0 text-left">スタッフ一覧</p>
                <staff-search @change="addSelectedStaff" :staffs="staffs" :clear-after-selected="true"
                  :disabled="isProcessing || !(inputs.selectedOfficeId)" />
                <staff-check-box-list v-model="inputs.selectedStaffs"
                  :disabled="isProcessing || !(inputs.selectedOfficeId)" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-10 h-83vh">
        <div class="card h-100">
          <div class="card-body staff-shift2"
               :class="{'staff-shift2-week': isWeekMode, 'staff-shift2-day': !isWeekMode}"
               @mousemove="mousemove($event)">
            <div class="tooltipevent" v-if="tip.active" :style="tipPlace">
              利用者：{{ tip.event.extendedProps.userDisplayName || tip.event.extendedProps.tag?.user?.display_name }}<br>
              時間：{{ tip.event.extendedProps.caretime }}<br>
              サービス：{{ tip.event.extendedProps.service }}<br>
            </div>
            <FullCalendar class="fc-shift2-calendar" ref="calendarRef" :options="calendarOptions" />
          </div>
        </div>
      </div>
    </div>
    <!-- <copy-day-modal @copy="onCopy" ref="copyDayModal" />
    <copy-week-modal @copy="copyWeek" ref="copyWeekModal" /> -->
    <plan-edit-modal v-if="showPlanEditModal"
      v-model="showPlanEditModal"
      :office-id="inputs.selectedOfficeId"
      :staffs="staffs || []"
      :users="users || []"
      :plan="plan"
      :editable="true"
      :params="params"
      :is-shift-mode="isShiftMode"
      @update:modelValue="val => showPlanEditModal = val"
      @close="onPlanEditModalClosed"
      @cancel="closeModal" />
    <CBModal :ref="el => dialog = el" />
  </div>
</template>

<script setup>
import SelectOffice from '@/components/SelectOffice'
import StaffSearch from '@/components/StaffSearch'
import StaffCheckBoxList from '@/components/StaffCheckBoxList'
import PlanEditModal from '@/components/PlanEditModal'
import FullCalendar from '@fullcalendar/vue3'
import interactionPlugin from '@fullcalendar/interaction'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import luxonPlugin from '@fullcalendar/luxon'
import locale from '@fullcalendar/core/locales/ja'
import _ from 'lodash'
import { useStore } from 'vuex'
import { ref, reactive, computed, watch, onMounted } from 'vue'
import dayjs from 'dayjs'
import constants from '@/constants'
import api from '@/libs/Api'
import utils from '@/libs/Utils'
import CBModal from '@/components/CBModal.vue'
import { useExecutor } from '@/libs/Executor'
import Constants from '@/constants'

const DATE_RANGE = 7

const store = useStore()
const { exec } = useExecutor()
const plan = ref([])
const showPlanEditModal = ref(false)
const dialog = ref(null)
const isShiftMode = ref(false)
const copyDayModal = ref(null)
const copyWeekModal = ref(null)
const isProcessing = ref(false)
const isWeekMode = ref(false)
const tipPlace = ref({
  top: 0,
  left: 0
})
const tip = reactive({
  active: false,
  event: null,
  place: null
})
const inputs = reactive({
  selectedOfficeId: null,
  selectedStaffs: [],
  userViewMode: false
})
const dateRange = reactive({
  startDate: null,
  endDate: null
})
const params = ref({
  start_day: null,
  start_hhmm: null,
  end_day: null,
  end_hhmm: null,
  staff_id: null,
  isShiftMode: false
})

// Add calendar ref
const calendarRef = ref(null)
const calendarEvents = ref([])
const calendarResources = ref([])

/**
 * Converts a plan object to calendar event format
 * @param {Object} plan - The plan data to convert
 * @returns {Array} Array of event objects for the calendar
 */
function convertPlan2Event (plan) {
  return _.map(plan.staff, staff => {
    const mark = plan.staff.length > 1 ? '*' : ''
    const staffId = staff.id.toString()
    return {
      id: `${plan.id}_${staffId}`,
      title: `${mark}${plan.user.display_name}`,
      groupId: plan.id,
      resourceId: staffId,
      start: utils.formatDay(plan.service.start_day) + 'T' + utils.formatHHMM(plan.service.start_hhmm) + ':00',
      end: utils.formatDay(plan.service.end_day) + 'T' + utils.formatHHMM(plan.service.end_hhmm) + ':00',
      tag: plan,
      userDisplayName: plan.user.display_name,
      service: convertService(plan.service),
      caretime: utils.formatHHMM(plan.service.start_hhmm) + ' ～ ' + utils.formatHHMM(plan.service.end_hhmm),
      color: utils.getEventColor(plan)
    }
  })
}


const convertService = (service) => {
  let isTeresa = (Constants.FunctionMask.TeresaStandard & profile.value.site.function_mask) === Constants.FunctionMask.TeresaStandard
  if (isTeresa) {
    if (service.service_teresa_unique_code) {
      return service.service_teresa_unique_code?.display_name
    }
  }
  if (service.service_code) {
    return service.service_code.display_name
  }

  return service.service_type.display_name
}

/**
 * Converts API result data to calendar events format
 * @param {Array} result - The API result containing plans
 * @returns {Array} Flattened array of calendar events
 */
function convertResultToEvents (result) {
  return _.chain(result)
    .map(plan => convertPlan2Event(plan))
    .flatten(true)
    .value()
}

// Computed properties
// eslint-disable-next-line no-unused-vars
const hasTarget = computed(() => {
  return inputs.selectedOfficeId && inputs.selectedStaffs.length > 0
})

// Get calendar api
const calendarApi = computed(() => {
  return calendarRef.value.getApi()
})

const queryParams = computed(() => {
  const staffIds = _.map(inputs.selectedStaffs, selectedStaff => selectedStaff.id)
  const params = {
    mode: constants.QUERY_MODE_SHIFT,
    start_day_from: dateRange.startDate ? dateRange.startDate.format('YYYYMMDD') : null,
    start_day_to: dateRange.endDate ? dateRange.endDate.format('YYYYMMDD') : null
  }
  if (inputs.selectedOfficeId) {
    params.office_id = inputs.selectedOfficeId
  }
  if (staffIds.length > 0) {
    params.staff_ids = staffIds
  }
  // 全利用者一覧で user_ids を常に送ると、一覧に含まれない利用者のプランが API 側で除外され [] になる。
  // 「担当利用者のみ」モードのときだけ絞り込み用に送る。
  if (inputs.userViewMode && users.value.length > 0) {
    params.user_ids = _.map(users.value, user => user.id)
  }
  return params
})

// Vuex state/getters
const selectedOfficeId = computed(() => store.state.staffShift.selectedOfficeId)
const profile = computed(() => store.state.session.profile)
const users = computed(() => store.getters['staffShift/users'])
const staffs = computed(() => store.getters['staffShift/staffs'])
const selectedStaffs = computed(() => store.getters['staffShift/selectedStaffs'])

// Date click event handler
const onDateClick = (info) => {
  if (isProcessing.value || inputs.selectedStaffs.length < 1) return

  // Get the staff ID from the resource if available
  let staffId = null
  if (info.resource && info.resource.id) {
    staffId = info.resource.id
  } else {
    // In case resource isn't available, try to determine which staff cell was clicked
    const resourceId = info.jsEvent?.target?.closest('.fc-timegrid-col')?.getAttribute('data-resource-id')
    if (resourceId) {
      staffId = resourceId
    } else if (inputs.selectedStaffs.length === 1) {
      // If only one staff is selected, default to that staff
      staffId = inputs.selectedStaffs[0].id.toString()
    } else {
      // If we can't determine the staff, show an alert
      dialog.value.show({
        title: '確認',
        text: 'スタッフが特定できません。1人のスタッフを選択してください。',
        clickToClose: true,
        buttons: [
          {
            title: '閉じる',
            handler: () => {
              dialog.value.hide()
            }
          }
        ]
      })
      return
    }
  }

  params.value = {
    start_day: dayjs(info.date).format('YYYY-MM-DD'),
    start_hhmm: dayjs(info.date).format('HHmm'),
    end_day: dayjs(info.date).format('YYYY-MM-DD'),
    end_hhmm: dayjs(info.date).add(30, 'minutes').format('HHmm'),
    staff_id: staffId
  }
  isShiftMode.value = true
  plan.value = null
  showPlanEditModal.value = true
}

// Event click outer handler (for status checks)
const onEventClickOuter = (info) => {
  if (isProcessing.value) return
  const prev = info.event.extendedProps.tag
  if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
    dialog.value.show({
      title: '確認',
      text: '承認が済んでいるプランです。',
      clickToClose: false,
      buttons: [
        {
          title: '閉じる',
          handler: () => {
            dialog.value.hide()
          }
        }
      ]
    })
  } else if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.REPORTED)) {
    dialog.value.show({
      title: '確認',
      text: `報告が済んでいるプランです。編集しますか？`,
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            onEventClick(info)
          }
        }
      ]
    })
  } else {
    onEventClick(info)
  }
}

// Event click event handler
const onEventClick = (info) => {
  params.value = null
  plan.value = info.event.extendedProps.tag
  isShiftMode.value = true
  showPlanEditModal.value = true
}

// Mouseover event handler
const onMouseover = (e) => {
  tip.active = true
  tip.event = e.event
}

// ドラッグ/リサイズ中は FullCalendar が eventMouseLeave を発火しないため、開始時にも呼ぶ
const hideTip = () => {
  tip.active = false
}

// Event resize event handler
const onEventResize = (info) => {
  if (isProcessing.value) return
  const prev = info.event.extendedProps.tag
  const endHH = dayjs(info.event.end).format('HHmm')
  const startHH = dayjs(info.event.start).format('HHmm')
  if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
    dialog.value.show({
      title: '確認',
      text: '承認が済んでいるプランです。',
      clickToClose: false,
      buttons: [
        {
          title: '閉じる',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        }
      ]
    })
  } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
    dialog.value.show({
      title: '確認',
      text: '報告が済んでいるプランです。時間を変更しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            const plan = convertEvent2Plan(info.event)
            registerPlan(plan)
          }
        }
      ]
    })
  } else if (endHH !== prev.service.end_hhmm) {
    dialog.value.show({
      title: '確認',
      text: '元の時間と異なりますが変更しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            const plan = convertEvent2Plan(info.event)
            registerPlan(plan)
          }
        }
      ]
    })
  } else if (startHH !== prev.service.start_hhmm) {
    dialog.value.show({
      title: '確認',
      text: '元の時間と異なりますが変更しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            const plan = convertEvent2Plan(info.event)
            registerPlan(plan)
          }
        }
      ]
    })
  }
}

// Event drop event handler
const onEventDropOuter = (info) => {
  if (isProcessing.value) return
  const prev = info.event.extendedProps.tag
  const startHH = dayjs(info.event.start).format('HHmm')
  const startDay = dayjs(info.event.start).format('YYYYMMDD')
  if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
    dialog.value.show({
      title: '確認',
      text: '承認が済んでいるプランです。',
      clickToClose: false,
      buttons: [
        {
          title: '閉じる',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        }
      ]
    })
  } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
    dialog.value.show({
      title: '確認',
      text: '報告が済んでいるプランです。時間を移動してもよろしいですか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            onEventDrop(info)
          }
        }
      ]
    })
  } else if (startDay !== prev.service.start_day || startHH !== prev.service.start_hhmm) {
    dialog.value.show({
      title: '確認',
      text: '元の時間と異なりますが移動しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            onEventDrop(info)
          }
        }
      ]
    })
  } else {
    onEventDrop(info)
  }
}

// Event drop event handler
const onEventDrop = (info) => {
  const plan = convertEvent2Plan(info.event)
  if (info.newResource) {
    const newStaff = _.find(staffs.value, staff => {
      return staff.id === Number(info.newResource.id)
    })
    if (!newStaff) return
    const planStaffList = _.filter(plan.staff, staff => {
      return staff.staff_id !== Number(info.oldResource.id)
    })
    planStaffList.push({ staff_id: newStaff.id })
    plan.staff = planStaffList
  }
  registerPlan(plan)
}

// Event dates render event handler
const datesRender = (info) => {
  // 週/日切替時はツールバーが再構築されるため、処理後に入力欄を差し込み直す
  setTimeout(setupJumpDateInput, 0)
  const newStart = dayjs(info.view.activeStart)
  const newEnd = dayjs(info.view.activeEnd).add(-1, 'days')
  const unchanged = dateRange.startDate
    && dateRange.startDate.format('YYYYMMDD') === newStart.format('YYYYMMDD')
    && dateRange.endDate
    && dateRange.endDate.format('YYYYMMDD') === newEnd.format('YYYYMMDD')
  dateRange.startDate = newStart
  dateRange.endDate = newEnd
  if (!unchanged) loadPlans()
}

const fetchCalendarEvents = (_fetchInfo, successCallback) => {
  successCallback(convertResultToEvents(calendarEvents.value))
}

// Calendar options as a computed property
const calendarOptions = computed(() => {
  return {
    schedulerLicenseKey: process.env.VUE_APP_SCHEDULER_LICENSE_KEY,
    datesAboveResources: true,
    editable: true,
    droppable: true,
    dayMaxEvents: true,
    nowIndicator: true,
    slotMinWidth: 20,
    // 帯の幅は実時間どおりにする(既定30pxだと短い予定が長く見える)。
    // 短時間サービスでも掴めるよう、CSS側で見えない掴み領域を広げている
    eventMinWidth: 0,
    slotMinTime: '00:00:00',
    slotMaxTime: '24:00:00',
    slotDuration: '00:30',
    slotLabelInterval: '01:00',
    initialView: 'resourceTimelineDay',
    firstDay: 1,
    initialDate: dayjs().toDate(),
    timeZone: 'Asia/Tokyo',
    plugins: [
      luxonPlugin,
      interactionPlugin,
      resourceTimelinePlugin
    ],
    resources: calendarResources.value,
    resourceOrder: 'sort',
    locale: locale,
    views: {
      resourceTimelineWeek: {
        type: 'resourceTimeline',
        duration: { days: DATE_RANGE },
        buttonText: '週',
        titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }
      },
      resourceTimelineDay: {
        type: 'resourceTimeline',
        duration: { days: 1 },
        buttonText: '日',
        titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }
      }
    },
    headerToolbar: {
      left: 'prev,today,next',
      center: 'title',
      right: 'resourceTimelineWeek,resourceTimelineDay'
    },
    events: fetchCalendarEvents,
    customButtons: {
      prev: {
        text: '<',
        click: function () {
          calendarApi.value.prev()
        }
      },
      today: {
        text: '今日',
        click: function () {
          calendarApi.value.today()
        }
      },
      next: {
        text: '>',
        click: function () {
          calendarApi.value.next()
        }
      },
      copy: {
        text: 'コピー',
        click: function () {
          if (isWeekMode.value) {
            showCopyWeekModal()
          } else {
            showCopyDayModal()
          }
        }
      },
      resourceTimelineWeek: {
        text: '週',
        click: function () {
          if (isWeekMode.value) return
          calendarApi.value.changeView('resourceTimelineWeek')
          calendarApi.value.gotoDate(dayjs().day(1).toDate())
          isWeekMode.value = true
          calendarApi.value.setOption('customButtons', {
            ...calendarApi.value.getOption('customButtons'),
            today: {
              text: '今週',
              click: function () {
                calendarApi.value.today()
              }
            }
          })
        }
      },
      resourceTimelineDay: {
        text: '日',
        click: function () {
          if (!isWeekMode.value) return
          calendarApi.value.changeView('resourceTimelineDay')
          calendarApi.value.gotoDate(dayjs().toDate())
          isWeekMode.value = false
          calendarApi.value.setOption('customButtons', {
            ...calendarApi.value.getOption('customButtons'),
            today: {
              text: '今日',
              click: function () {
                calendarApi.value.today()
              }
            }
          })
        }
      }
    },
    resourceAreaHeaderContent: 'スタッフ',
    resourceAreaWidth: '150px',
    dateClick: onDateClick,
    eventClick: onEventClickOuter,
    eventMouseEnter: onMouseover,
    eventMouseLeave: hideTip,
    eventDragStart: hideTip,
    eventResizeStart: hideTip,
    eventResize: onEventResize,
    eventDrop: onEventDropOuter,
    datesSet: datesRender
  }
})

/**
     * Loads plans from API based on current parameters
     * @returns {Promise} Promise resolving to loaded plans
     */
const loadPlans = () => {
  return exec(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return

    if (!inputs.selectedOfficeId || inputs.selectedStaffs.length < 1) {
      calendarEvents.value = []
      return
    }

    if (inputs.userViewMode && users.value.length < 1) {
      calendarEvents.value = []
      return
    }

    isProcessing.value = true
    try {
      const result = await api.getPlans(queryParams.value)
      calendarEvents.value = result || []
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Handler for copy completion event
     * @param {*} dst - Destination data (unused)
     */
// eslint-disable-next-line no-unused-vars
const onCopy = (dst) => {
  loadPlans()
}

/**
     * Handler for plan edit modal close
     * @param {boolean} updated - Whether the plan was updated
     */
const onPlanEditModalClosed = (updated) => {
  if (!updated) return
  loadPlans()
}

/**
     * Adds a staff to the selected staff list
     * @param {Object} selectedStaff - Staff object to add
     */
const addSelectedStaff = (selectedStaff) => {
  if (!selectedStaff) return

  const index = _.findIndex(inputs.selectedStaffs, staff => staff.id === selectedStaff.id)
  if (index >= 0) return

  // Create a new array instead of directly pushing to maintain reactivity in Vue 3
  inputs.selectedStaffs = utils.sortStaffsByKana([...inputs.selectedStaffs, selectedStaff])

  // Load plans with the new staff selection
  loadPlans()
}

/**
     * Copies a week's shifts to another week
     * @param {Object} destWeek - Destination week
     */
// eslint-disable-next-line no-unused-vars
const copyWeek = (destWeek) => {
  exec(async () => {
    isProcessing.value = true
    try {
      const params = queryParams.value
      params.dest_start_day_from = destWeek.format('YYYYMMDD')
      await api.copyShiftWeek(params)
      await loadPlans()
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Shows the copy week modal
     */
const showCopyWeekModal = () => {
  if (isProcessing.value) return
  copyWeekModal.value.show({
    weekStart: dateRange.startDate
  })
}

/**
     * Shows the copy day modal
     */
const showCopyDayModal = () => {
  if (isProcessing.value) return
  copyDayModal.value.show({
    targetDay: dateRange.startDate
  })
}

/**
     * Copies a day's shifts to another day
     * @param {Object} destDay - Destination day
     */
// eslint-disable-next-line no-unused-vars
const copyDay = (destDay) => {
  exec(async () => {
    isProcessing.value = true
    try {
      const params = queryParams.value
      params.dest_start_day_from = destDay.format('YYYYMMDD')
      await api.copyShiftDay(params)
      await loadPlans()
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Registers/saves a plan
     * @param {Object} plan - Plan object to save
     */
const registerPlan = (plan) => {
  exec(async () => {
    isProcessing.value = true
    try {
      await store.dispatch('careplan/savePlan', plan)
      await loadPlans()
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Handler for user view mode change
     * @returns {Promise} Promise resolving after mode change
     */
const onChangeUserViewMode = () => {
  return exec(async () => {
    await store.dispatch('staffShift/loadUsers', { officeId: inputs.selectedOfficeId, userViewMode: inputs.userViewMode })
    return loadPlans()
  })
}

/**
     * Handler for office selection
     * @param {string|number} officeId - ID of the selected office
     */
const onOfficeSelected = (officeId) => {
  if (officeId) {
    loadPlans()
  }
}

// ISO週文字列(例: 2026-W31)を生成する。mondayは週の月曜
const toIsoWeekString = (monday) => {
  // その週の木曜が属する年がISO週の年になる
  const thursday = monday.add(3, 'day')
  const week = Math.ceil((thursday.diff(thursday.startOf('year'), 'day') + 1) / 7)
  return `${thursday.year()}-W${String(week).padStart(2, '0')}`
}

// ISO週文字列からその週の月曜を求める
const fromIsoWeekString = (value) => {
  const m = value.match(/^(\d{4})-W(\d{2})$/)
  if (!m) return null
  // 1/4は必ず第1週に含まれるので、その週の月曜を起点に週数分進める
  const jan4 = dayjs(new Date(Number(m[1]), 0, 4))
  const week1Monday = jan4.subtract(jan4.day() === 0 ? 6 : jan4.day() - 1, 'day')
  return week1Monday.add((Number(m[2]) - 1) * 7, 'day')
}

// 表示日付の指定で該当日にジャンプする
const jumpToDate = (value, isWeekValue) => {
  let date
  if (isWeekValue) {
    date = fromIsoWeekString(value)
  } else {
    date = dayjs(value)
    // 週表示のときは選択日を含む週(月曜はじまり)の先頭に移動する
    if (date.isValid() && isWeekMode.value) {
      date = date.subtract(date.day() === 0 ? 6 : date.day() - 1, 'day')
    }
  }
  if (!date || !date.isValid()) return
  calendarApi.value.gotoDate(date.toDate())
}

// ツールバーの「<」「>」の間に表示日付の入力欄を差し込み、表示中の日付/週を反映する。
// FullCalendarのヘッダーツールバーはボタンしか定義できないため、
// レンダリング後にDOMへ挿入する(datesSetのたびに呼ばれる)
const setupJumpDateInput = () => {
  const root = document.querySelector('.staff-shift2')
  if (!root) return
  let input = root.querySelector('.fc-jump-date-input')
  if (!input) {
    const nextButton = root.querySelector('.fc-next-button')
    if (!nextButton) return
    input = document.createElement('input')
    input.className = 'fc-jump-date-input'
    input.title = '日付を指定して表示'
    input.addEventListener('change', () => {
      if (input.value) jumpToDate(input.value, input.type === 'week')
    })
    nextButton.parentNode.insertBefore(input, nextButton)
  }
  // 週表示のときは週選択、日表示のときは日付選択にする
  input.type = isWeekMode.value ? 'week' : 'date'
  // type='week'非対応ブラウザ(Safari等)では日付選択にフォールバックする
  const isWeekInput = input.type === 'week'
  if (!isWeekInput) input.type = 'date'
  // 矢印や今週/今日での移動にも追従して表示中の日付/週を反映する
  if (dateRange.startDate) {
    input.value = isWeekInput
      ? toIsoWeekString(dateRange.startDate)
      : dateRange.startDate.format('YYYY-MM-DD')
  }
}

// Event handlers
const mousemove = (e) => {
  tipPlace.value.top = (e.clientY - e.offsetY) - 70 + 'px'
  tipPlace.value.left = (e.clientX - e.offsetX) - 450 + 'px'
}

/**
     * Converts a calendar event to plan format
     * @param {Object} event - Calendar event to convert
     * @returns {Object} Plan object
     */
const convertEvent2Plan = (event) => {
  const plan = event.extendedProps.tag
  return {
    id: plan.id,
    user_id: plan.user_id,
    office_id: plan.office_id,
    staff: _.map(plan.staff, staff => {
      return { staff_id: staff.id }
    }),
    service: {
      service_type_id: plan.service.service_type_id,
      start_day: dayjs(event.start).format('YYYYMMDD'),
      start_hhmm: dayjs(event.start).format('HHmm'),
      end_day: dayjs(event.end).format('YYYYMMDD'),
      end_hhmm: dayjs(event.end).format('HHmm')
    }
  }
}

// Watchers
watch(() => inputs.selectedOfficeId, (selectedOfficeId) => {
  exec(() => {
    isProcessing.value = true
    return store.dispatch('staffShift/selectOffice', {
      officeId: selectedOfficeId,
      userViewMode: inputs.userViewMode
    })
      .then(() => {
        inputs.selectedOfficeId = selectedOfficeId
        inputs.selectedStaffs = selectedStaffs.value
      })
      .finally(() => {
        isProcessing.value = false
      })
  })
})

watch(() => inputs.selectedStaffs, (newSelectedStaffs) => {
  // Update resources for calendar（resourceOrder: 'sort' 用に sort を付与）
  calendarResources.value = utils.sortStaffsByKana(newSelectedStaffs).map((staff, index) => {
    return {
      id: staff.id.toString(), // Ensure ID is a string
      title: staff.display_name,
      sort: index
    }
  })

  // Update store state
  store.dispatch('staffShift/setSelectedStaffs', {
    officeId: inputs.selectedOfficeId,
    staffs: newSelectedStaffs
  })

  if (newSelectedStaffs.length > 0) {
    loadPlans()
  } else {
    calendarEvents.value = []
    calendarRef.value?.getApi()?.refetchEvents()
  }
}, { deep: true })

watch(() => users.value, (newUsers) => {
  if (inputs.userViewMode && newUsers && newUsers.length > 0) {
    loadPlans()
  }
})

watch(calendarEvents, () => {
  calendarRef.value?.getApi()?.refetchEvents()
})

watch(isProcessing, (val) => {
  const api = calendarRef.value?.getApi()
  if (api) {
    api.setOption('editable', !val)
    api.setOption('droppable', !val)
  }
})

// Lifecycle hooks
onMounted(() => {
  exec(async () => {
    inputs.selectedOfficeId = selectedOfficeId.value

    // Initialize dateRange with default values (current week)
    if (!dateRange.startDate || !dateRange.endDate) {
      const today = dayjs()
      // We want to make sure today is visible in the view
      const currentDay = today.day()
      // Calculate to get the nearest Monday
      const diff = currentDay === 0 ? 6 : currentDay - 1
      const weekStart = today.subtract(diff, 'day')
      const weekEnd = weekStart.add(6, 'days')

      dateRange.startDate = weekStart
      dateRange.endDate = weekEnd
    }

    await store.dispatch('staffShift/initStaffShift')

    // Ensure initial office selection is set from store
    if (selectedOfficeId.value && !inputs.selectedOfficeId) {
      inputs.selectedOfficeId = selectedOfficeId.value

      // If office is selected but no staffs, try to get staffs from store
      if (selectedStaffs.value && selectedStaffs.value.length > 0) {
        inputs.selectedStaffs = selectedStaffs.value
      }
    }
  })
})
</script>

<style lang="scss">
// ツールバー内の表示日付入力
// 短時間サービスの帯でもドラッグ移動できるよう、リサイズハンドル
// (既定8px・帯の内側に4px食い込む)を細くして掴み領域を確保する
.staff-shift2 .fc-timeline-event {
  --fc-event-resizer-thickness: 4px;

  // 帯の見た目は実時間どおりの幅のまま、ポインタが反応する領域だけ
  // 最低20px確保する(帯中央基準で左右に広がる透明なあたり判定)
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    min-width: 20px;
  }

  // リサイズハンドルはあたり判定より手前に置き、時間変更操作も維持する
  .fc-event-resizer {
    z-index: 5;
  }
}

.staff-shift2 .fc-jump-date-input {
  background: #ffffff;
  border: 1px solid rgba(120, 130, 140, 0.13);
  border-radius: 2px;
  color: #67757c;
  height: 2.1em;
  margin: 0 4px;
  padding: 0 6px;
  vertical-align: middle;
}

.fc-shift2-calendar {
  height: 99%;
  margin: 5px;

  /* ここで overflow を付けると、ScrollGrid のヘッダー／ボディの横スクロール同期が崩れ、
     「時刻行」と「プラン行」の X がずれて見える（下端の横スクロールバー表示時に顕著） */

  .fc-scrollgrid {
    border-bottom: 1px solid #ddd !important;
  }

  .fc-scrollgrid-section-body table {
    border-bottom: none !important;
  }

  .fc-timeline-slots tbody tr {
    min-height: 30px;
    height: 30px;
  }

  .fc-timeline-header-row-chrono .fc-timeline-slot-frame {
    justify-content: flex-start !important;
  }

  .fc-timeline-slot-frame {
    justify-content: center !important;
  }

  .fc-resource-timeline .fc-resource-area {
    overflow-y: hidden;
    position: sticky;
    left: 0;
    z-index: 2;
    background-color: #fff;
    border-right: 1px solid #ddd;
  }

  .fc-timeline-slots tr:last-child,
  .fc-timeline-slots tr:last-child td,
  .fc-datagrid-body tr:last-child,
  .fc-datagrid-body tr:last-child td,
  .fc-datagrid-body .fc-datagrid-cell:last-child,
  .fc-timeline-body .fc-timeline-slot:last-child {
    border-bottom: 1px solid #ddd !important;
  }

  .fc-timeline-slot-frame a {
    color: black !important;
    font-size: 13px;
    font-weight: 300;
    line-height: 20px;
    text-transform: uppercase;
    text-decoration: none !important;
  }

  .fc-resourceTimelineWeek-button,
  .fc-resourceTimelineDay-button {
    background: #ffffff !important;
    border: 1px solid rgba(120, 130, 140, 0.13) !important;
    color: #99abb4 !important;
    text-transform: capitalize !important;
    height: 2.1em !important;
    &:hover {
      background: #f2f4f8 !important;
      opacity: 0.8 !important;
    }
  }

  .fc-event {
    white-space: normal;
    line-height: 1.25;
  }
}
</style>
